import { memo, useCallback, useMemo, useState } from 'react';
import { FilterSlideout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { buildMarketplaceFilterSlots } from '../utils/buildMarketplaceFilterSlots.jsx';

/**
 * Marketplace Filters slideout (CT-4168): a right-side `FilterSlideout` with five
 * collapsible sections. Draft mode — selections update a local draft only; the
 * grid changes on Apply (commits to `useMarketplace.setFilters`, CT-4166). Cancel
 * (onClose) discards the draft; Clear All resets it.
 */
function MarketplaceFiltersSlideout({ isOpen, onClose, appliedFilters, onApply }) {
  const { t } = useTranslation('admin-studio');
  const [draft, setDraft] = useState(appliedFilters ?? {});

  // Reset the draft to the applied filters on each open transition (discard on
  // Cancel). Adjusting state during render on a tracked prop change is React's
  // recommended alternative to a setState-in-effect (which cascades renders).
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setDraft(appliedFilters ?? {});
  }

  const setField = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleApply = useCallback(() => {
    // Drop empty values so activeFilterCount stays accurate: empty arrays
    // (multi-select) and empty strings (single-select Used-in-studio / Source).
    const cleaned = Object.fromEntries(
      Object.entries(draft).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : Boolean(v))),
    );
    onApply?.(cleaned);
    onClose?.();
  }, [draft, onApply, onClose]);

  const sections = useMemo(
    () => buildMarketplaceFilterSlots(draft, setField, t),
    [draft, setField, t],
  );

  return (
    <FilterSlideout
      isOpen={isOpen}
      onClose={onClose}
      title={t('integrations.marketplace.filters.title')}
      sections={sections}
      onClearAll={() => setDraft({})}
      onApply={handleApply}
    />
  );
}

export default memo(MarketplaceFiltersSlideout);
